<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { useRoute } from 'vue-router'

    import { dynamicUiRoutes } from '@common/dynamicUiRoutes'

    import type { DependentEntityInfo } from '@common/types'
    import type { EvalModelDeleterViewModel } from '@Evaluation/EvaluationModels/Components/EvalModelDeleter'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: EvalModelDeleterViewModel
    }>()

    const emit = defineEmits<{
        (e: 'delete-evalmodel', evalModelId: number): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')
    const route = useRoute()

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const initialized = computed(() => vm.value.initialized)

    const goalName = computed(() => vm.value.goalName)
    const hasDependants = computed(() => vm.value.hasDependants)
    const dependenciesCount = computed(() => vm.value.dependenciesCount)
    const weightedScores = computed<DependentEntityInfo[]>(() => vm.value.weightedScores)

    // ---------------- helpers ----------------

    function url_EditWeightedScorePage(e: DependentEntityInfo)
    {
        return dynamicUiRoutes.Criteria_EditWeightedScorePage2(
            route,
            e.WorkspaceId,
            e.TchProjectId,
            Number.parseInt(e.Id)
        )
    }

    // ---------------- actions ----------------

    function handleDeleteEvalModelCommand()
    {
        emit('delete-evalmodel', vm.value.id)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div v-if="!initialized" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete goal</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h4><i class="fa fa-spinner fa-spin"></i> Checking dependencies...</h4>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>

            <div v-if="initialized && hasDependants" class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete goal</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="alert alert-danger text-dark" role="alert">
                            <h5 class="alert-heading">Cannot delete this goal</h5>
                            <p>
                                The goal '<strong>{{goalName}}</strong>' is used in <strong>{{dependenciesCount}}</strong> places.
                                To delete this goal, first remove it from where it is used.
                            </p>
                        </div>
                        <div>
                            <div class="card" v-if="weightedScores.length > 0">
                                <div class="card-header">
                                    Weighted scores (<span>{{weightedScores.length}}</span>)
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="ws in weightedScores" :key="ws.Id">
                                        <a class="text-primary" v-sanitizedHref="url_EditWeightedScorePage(ws)">{{ws.Name}}</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>

            <div v-if="initialized && !hasDependants" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title">Delete goal</h5>
                        <button type="button" class="close text-white" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h4>Are you sure you want to delete this goal?</h4>
                        <p>You are about to delete '<strong>{{goalName}}</strong>'. This can't be undone.</p>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="handleDeleteEvalModelCommand" class="btn btn-danger">Delete</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>